import React, { useEffect, useState } from "react";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { SpinnerWrapper } from "../SpinnerWrapper";
import PositionService from "../../services/PositionService";
import Position from "../../models/Position";
import { EditDialog } from "../../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import PositionFormFields from "./Fields/PositionFormFields";
import WorkLogService from "../../services/WorkLogService";
import AutoCompleteUIDTO from "../../dto/AutoCompleteUIDTO";

interface Props {
  externalId?: string;
  editMode?: boolean;
  onClose: (action: "update" | "create" | "canceled") => void;
  userNames?: AutoCompleteUIDTO[];
  projectExternalId?: string;
}

function PositionFormDialog({ externalId, editMode = true, onClose, userNames, projectExternalId }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [instance, setInstance] = useState<Position | undefined>();
  const [showDialog, setShowDialog] = useState(true);
  const [hasWorklogs, setHasWorkLogs] = useState(false);
  const [newBillingPolicy, setNewBillingPolicy] = useState(false);
  const [shadowChecked, setShadowChecked] = useState(false);
  const [bonusValue, setBonusValue] = useState(0);
  useEffect(() => {
    if (editMode && externalId) {
      fetchProject(externalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const positionHasWorkLogs = async (externalId: string) => {
    const workLogs = (await WorkLogService.findByPositionExternalId(externalId)).getContent();
    return workLogs.length > 0;
  };

  const fetchProject = async (externalId: string) => {
    setIsLoading(true);
    try {
      const position = (await PositionService.getByExternalId(externalId)).getContent();
      setInstance(position);
      setHasWorkLogs(await positionHasWorkLogs(externalId));
      setNewBillingPolicy(position.billingPolicy.toString() === "SUM_HOURS_WITH_MINIMUM");
    } catch (error: any) {
      NotificationService.error("Error getting project");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeBillingPolicy = (event: any) => {
    const value = event.target.value;
    setNewBillingPolicy(value === "SUM_HOURS_WITH_MINIMUM");
    setBonusValue(0);
  };

  const handleCheckShadow = () => {
    setShadowChecked(!shadowChecked);
    setBonusValue(0);
  };

  const handleChangeBonus = (event: any) => {
    const newValue = parseInt(event.target.value);
    setBonusValue(newValue);
    setShadowChecked(false);
  };

  const handleUpdatePosition = async (position: Position) => {
    setShowDialog(false);
    try {
      await PositionService.update(position);
      onClose("update");
    } catch (error: any) {
      NotificationService.error(error.response.data.message);
      onClose("canceled");
    }
  };

  const handleCreatePosition = async (position: Position) => {
    try {
      position.projectExternalId = projectExternalId;
      await PositionService.create(position);
      onClose("create");
    } catch (error: any) {
      NotificationService.error(error.response.data.message);
      onClose("canceled");
    }
  };

  const handleOnCancel = () => {
    setShowDialog(false);
    setHasWorkLogs(false);
    setBonusValue(0);
    onClose("canceled");
  };

  return (
    <SpinnerWrapper isLoading={isLoading}>
      <EditDialog<Position>
        elementName={"Position"}
        dialogPosition={DialogPosition.Center}
        currentInstance={instance}
        newItemDialogOpen={showDialog}
        onUpdate={(position) => {
          handleUpdatePosition(position);
        }}
        onCreate={(position) => {
          handleCreatePosition(position);
        }}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <PositionFormFields
            control={control}
            register={register}
            errors={errors}
            newBillingPolicy={newBillingPolicy}
            onChangeBillingPolicy={handleChangeBillingPolicy}
            disabledBonusHours={shadowChecked}
            onChangeBonus={handleChangeBonus}
            readonlyShadowCheckbox={hasWorklogs || bonusValue > 0}
            onCheckShadow={handleCheckShadow}
            isEditing={editMode}
            userNames={userNames}
          />
        )}
        buttonStyles={{}}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />
    </SpinnerWrapper>
  );
}

export default PositionFormDialog;
